import React, { useState, useEffect } from "react";
import { debugInfo, debugLog } from "../utils/debug";
import { getSbisSettings, saveSbisSettings } from "../utils/sbisSettings";
import { API_BASE_URL } from "../config/api";

const SettingsModal = ({ isOpen, onClose }) => {
  const [sbisSettings, setSbisSettings] = useState({
    login: "",
    password: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      debugInfo("SettingsModal", "Loading SBIS settings from S3");
      
      let token = localStorage.getItem("authToken");
      if (token && token.startsWith("lx_access_")) {
        token = token.substring(10);
      }
      const response = await fetch(`${API_BASE_URL}/user/sbis-settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const savedSettings = await response.json();
        if (savedSettings) {
          setSbisSettings({
            login: savedSettings.login || "",
            password: "",
          });
          debugInfo("SettingsModal", "SBIS settings loaded from S3");
        }
      }
    } catch (error) {
      debugLog("SettingsModal", "Error loading settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSbisSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Очищаем сообщение при изменении полей
    if (message.text) {
      setMessage({ type: "", text: "" });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ type: "", text: "" });

    try {
      debugInfo("SettingsModal", "Saving SBIS settings to S3");

      let token = localStorage.getItem("authToken");
      if (token && token.startsWith("lx_access_")) {
        token = token.substring(10);
      }
      const response = await fetch(`${API_BASE_URL}/user/sbis-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sbisSettings),
      });

      if (response.ok) {
        setMessage({
          type: "success",
          text: "Настройки СБиС успешно сохранены",
        });
        
        debugInfo("SettingsModal", "SBIS settings saved to S3");
        setSbisSettings((prev) => ({ ...prev, password: "" }));
      } else {
        const error = await response.json();
        setMessage({
          type: "error",
          text: error.error || "Не удалось сохранить настройки",
        });
      }
    } catch (error) {
      debugLog("SettingsModal", "Save settings error:", error);
      setMessage({
        type: "error",
        text: "Произошла ошибка при сохранении настроек",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setMessage({ type: "", text: "" });
    setSbisSettings({ login: "", password: "" });
    setShowPassword(false);
    onClose();
  };

  const handleShowSavedPassword = async () => {
    try {
      let token = localStorage.getItem("authToken");
      if (token && token.startsWith("lx_access_")) {
        token = token.substring(10);
      }
      const response = await fetch(`${API_BASE_URL}/user/sbis-settings?showPassword=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const savedSettings = await response.json();
        if (savedSettings && savedSettings.password) {
          setSbisSettings((prev) => ({
            ...prev,
            password: savedSettings.password,
          }));
          setShowPassword(true);
        }
      }
    } catch (error) {
      debugLog("SettingsModal", "Error showing password:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚙️ Настройки</h2>
          <button className="modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        {isLoading ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Загрузка настроек...</p>
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="modal-body">
              <div className="settings-section">
                <h3>Доступ к СБиС</h3>
                <p className="settings-description">
                  Настройте учетные данные для автоматического входа в систему
                  СБиС
                </p>

                {message.text && (
                  <div className={`message ${message.type}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="sbis-login">Логин СБиС:</label>
                  <input
                    type="text"
                    id="sbis-login"
                    name="login"
                    value={sbisSettings.login}
                    onChange={handleInputChange}
                    placeholder="Введите логин для СБиС"
                    disabled={isSaving}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sbis-password">Пароль СБиС:</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="sbis-password"
                      name="password"
                      value={sbisSettings.password}
                      onChange={handleInputChange}
                      placeholder="Введите пароль для СБиС"
                      disabled={isSaving}
                    />
                    {!sbisSettings.password && (
                      <button
                        type="button"
                        className="btn-show-password"
                        onClick={handleShowSavedPassword}
                        title="Показать сохранённый пароль"
                      >
                        👁️
                      </button>
                    )}
                  </div>
                  <small className="form-hint">
                    Оставьте пустым, если не хотите изменять текущий пароль
                  </small>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SettingsModal;
